import React, { useEffect } from "react";
import CodeBlock from "./CodeBlock";
import docStyles from "../../css/Docs/Docs.module.css";
import { Divider } from "@mui/material";
import DocWrapper from "./DocWrapper";
import { Link } from "react-router-dom";

const UpdateDemand = () => {
  useEffect(() => {
    const el = document.getElementById("introduction");
    if (el) {
      el.scrollIntoView();
    }
  }, []);

  const curlSample = `
~~~curl
curl --location --request PUT 'https://test.eqlfin.com/activities/update'
--header 'Content-Type: application/json' 
--header 'Firebase-Env: env/core' 
--header 'Authorization: Basic {{token}}' 
--data '{
    "share": [],
    "timestamp": 1682899381000,
    "template": "demand",
    "activityId" : "Q8txSHTdkWjUKZ5hEeCL",
    "schedule": [
        {
            "name": "Invoice Date",
            "startTime": 1682899381000,
            "endTime": 1682899391000
        }
    ],
    "venue": [],
    "attachment": {
        "tds": {
            "type": "number",
            "value": 0
        },
        "irn": {
            "type": "string",
            "value": ""
        },
        "narration": {
            "type": "string",
            "value": ""
        },
        "dueDate": {
            "type": "string",
            "value": "1688124200000"
        },
        "name": {
            "type": "string",
            "value": "SUN/2023/05"
        },
        "billingDate": {
            "type": "number",
            "value": 1682899381000
        },
        "totalAmount": {
            "type": "number",
            "value": 206000
        },
        "sgst": {
            "type": "number",
            "value": 3000
        },
        "billingCustomer": {
            "type": "gst",
            "value": "CUSTOMER_GST"
        },
        "billingBranch": {
            "type": "gst-office",
            "value": "YOUR_GST"
        },
        "cgst": {
            "type": "number",
            "value": 3000
        },
        "moratium": {
            "type": "number",
            "value": 0
        },
        "igst": {
            "type": "number",
            "value": 0
        }
    },
    "office": "YOUR_OFFICE_NAME"
}'
~~~
`;

  return (
    <DocWrapper>
      <div className={docStyles.container}>
        <section id="introduction" className={docStyles.section}>
          <div className={docStyles.heading}>Introduction</div>
          <div className={docStyles.para}>
            This API allows registered clients to update a demand using the
            provided activity ID. Access to creating customers and demands is
            limited to registered clients.
          </div>
        </section>

        <br />
        <Divider sx={{ width: "100%" }} />

        <section id="fields" className={docStyles.section}>
          <div className={docStyles.heading}>Important Fields</div>
          <div className={docStyles.para}>
            To create a demand for an existing office, the following fields are
            important:
            <ul
              style={{
                padding: "0px 0px 5px 10px",
                margin: "5px 0px 5px 10px",
              }}
            >
              <li>schedule</li>
              <li>activityId</li>
              <li>dueDate</li>
              <li>name</li>
              <li>billingDate</li>
              <li>totalAmount</li>
              <li>billingCustomer</li>
              <li>billingBranch</li>
              <li>Office</li>
            </ul>
          </div>
        </section>
        <br />
        <Divider sx={{ width: "100%" }} />

        <section id="request-example" className={docStyles.section}>
          <div className={docStyles.heading}>Request Example</div>
          <div className={docStyles.para}>
            This is a sample curl request
            <CodeBlock codeMarkdown={curlSample} />
          </div>

          <div className={docStyles.heading} id="schema-description">
            Schema Description
          </div>
          <table className={docStyles.markDownTable}>
            <thead>
              <tr>
                <th>Key</th>
                <th>Value</th>
                <th>Data Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>share</code>
                </td>
                <td>empty array</td>
                <td>array</td>
                <td>sharable documents</td>
              </tr>
              <tr>
                <td>
                  <code>timestamp</code>
                </td>
                <td>1683971979663</td>
                <td>number</td>
                <td>time of the creation</td>
              </tr>
              <tr>
                <td>
                  <code>template</code>
                </td>
                <td>demand</td>
                <td>String</td>
                <td>structure of the API call, here Demand/customer</td>
              </tr>
              <tr>
                <td>
                  <code>activityId</code>
                </td>
                <td>K3rsy45zrls0kVANgbkh</td>
                <td>String</td>
                <td>activity id</td>
              </tr>

              <tr>
                <td>
                  <code>venue</code>
                </td>
                <td>[ ]</td>
                <td>array</td>
                <td></td>
              </tr>
              <tr>
                <td>
                  <code>office</code>
                </td>
                <td>KAVYA COSMETICS</td>
                <td>String</td>
                <td>name of the requesting office</td>
              </tr>
            </tbody>
          </table>
          <div id="schedule" className={docStyles.headingTwo}>
            schedule
          </div>
          <table className={docStyles.markDownTable}>
            <thead>
              <tr>
                <th>Key</th>
                <th>Value (default)</th>
                <th>Data Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>name</code>
                </td>
                <td>Invoice Date</td>
                <td>string</td>
                <td>date of the invoice added</td>
              </tr>
              <tr>
                <td>
                  <code>startTime</code>
                </td>
                <td>1682899381000</td>
                <td>number</td>
                <td>time of the invoice creation</td>
              </tr>
              <tr>
                <td>
                  <code>endTime</code>
                </td>
                <td>1682899391000</td>
                <td>number</td>
                <td>time of the invoice creation</td>
              </tr>
            </tbody>
          </table>
          <div id="attachments" className={docStyles.headingTwo}>
            attachments
          </div>
          <table className={docStyles.markDownTable}>
            <thead>
              <tr>
                <th>Key</th>
                <th>Value (default)</th>
                <th>Data Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>sgst</code>
                </td>
                <td>0</td>
                <td>number/int/float</td>
                <td>value of the sgst</td>
              </tr>
              <tr>
                <td>
                  <code>tds</code>
                </td>
                <td>0</td>
                <td>number/int/float</td>
                <td>value of the tds</td>
              </tr>
              <tr>
                <td>
                  <code>irn</code>
                </td>
                <td>can be empty</td>
                <td>String</td>
                <td></td>
              </tr>
              <tr>
                <td>
                  <code>dueDate</code>
                </td>
                <td>1687804200000</td>
                <td>String</td>
                <td>due date for payment</td>
              </tr>
              <tr>
                <td>
                  <code>cgst</code>
                </td>
                <td>0</td>
                <td>number/int/float</td>
                <td>value of the cgst</td>
              </tr>
              <tr>
                <td>
                  <code>igst</code>
                </td>
                <td>9900</td>
                <td>number/int/float</td>
                <td>value of the igst</td>
              </tr>
              <tr>
                <td>
                  <code>billingDate</code>
                </td>
                <td>0</td>
                <td>number/int/float</td>
                <td></td>
              </tr>
              <tr>
                <td>
                  <code>totalAmount</code>
                </td>
                <td>64900</td>
                <td>number</td>
                <td>total amount on the invoice</td>
              </tr>
              <tr>
                <td>
                  <code>billingCustomer</code>
                </td>
                <td>GST_NUMBER</td>
                <td>gst number</td>
                <td>gst number of the billing customer</td>
              </tr>
              <tr>
                <td>
                  <code>moratium</code>
                </td>
                <td>0</td>
                <td>number/int/float</td>
                <td></td>
              </tr>
              <tr>
                <td>
                  <code>narration</code>
                </td>
                <td>can be empty</td>
                <td>String</td>
                <td></td>
              </tr>
              <tr>
                <td>
                  <code>billingBranch</code>
                </td>
                <td>GST_NUMBER</td>
                <td>gst-office</td>
                <td>gst number of the requesting customer</td>
              </tr>
              <tr>
                <td>
                  <code>name</code>
                </td>
                <td>KC/23-24/305123123</td>
                <td>String</td>
                <td>vendor code</td>
              </tr>
            </tbody>
          </table>

          <br />
          <Divider sx={{ width: "100%" }} />

          <section id="response" className={docStyles.section}>
            <div className={docStyles.heading}>Response</div>
            <div className={docStyles.para}>
              Upon successful execution of the Update Demand API, the API will
              return a response containing the updated demand's activity ID or
              any other relevant information associated with the demand.
            </div>

            <div className={docStyles.subHeading}>201 - Created</div>
            <CodeBlock
              codeMarkdown={`
~~~json
{
    "activityId": "wTtWcTBccQDQ8pXRsfLw",
    "message": "",
    "success": true,
    "code": 201
}

~~~
`}
            />
          </section>
          <br />
          <Divider sx={{ width: "100%" }} />

          <section id="error" className={docStyles.section}>
            <div className={docStyles.heading}>Error Responses</div>

            <div className={docStyles.para}>
              In case of errors during the API call, you may receive one of the
              following error responses:
              <ul
                style={{
                  padding: "0px 0px 5px 10px",
                  margin: "5px 0px 5px 10px",
                }}
              >
                <li>
                  <b>Unauthorized Error: </b> Occurs when the request lacks
                  valid authentication or authorization credentials.
                </li>
                <li>
                  <b> Server Error: </b> Indicates an unexpected error on the
                  server side, such as a database connection issue or an
                  unhandled exception.
                </li>
              </ul>
            </div>
          </section>
        </section>
      </div>
      <section className={docStyles.tableOfContentSection}>
        <div className={docStyles.tableOfContent}>
          <div className={docStyles.tocHeading}>Table of contents</div>
          <ul className={docStyles.tocList}>
            <li>
              <Link to="#introduction">Introduction</Link>
            </li>
            <li>
              <Link to="#fields">Important Fields</Link>
            </li>
            <li>
              <Link to="#request-example">Request Example</Link>
            </li>
            <li>
              <Link to="#schema-description">Schema Description</Link>
            </li>
            <li>
              <Link to="#response">Response</Link>
            </li>
            <li>
              <Link to="#error">Error Response</Link>
            </li>
          </ul>
        </div>
      </section>
    </DocWrapper>
  );
};

export default UpdateDemand;
